import { Vue, Component, Mixins, Prop } from "vue-property-decorator";
import { Action } from 'vuex-class';
import { VNode } from 'vue';
import { clone } from 'lodash';
import { sequentialMap, sequentialMapEvery } from '@/lib/helpers/async';
import CavAbstractDocument from "@/lib/models/cav-abstract-document";
import Uploader from "@/components/uploader";
import {uploadDoc } from "@/lib/services";
import LightBox from '@/components/lightbox';
import Icon from './icon';
import RightsAware from '@/lib/mixins/rights-aware';
import { loadingClass } from '@/lib/helpers';

@Component
export default class SlidePanel extends Mixins(RightsAware) {

  protected isOpen = false;

  get rootClasses() {
    return [];
  }

  get headClasses() {
    return [];
  }

  get bodyClasses() {
    return [];
  }

  get footClasses() {
    return [];
  }

  renderHead(): any {
    return this.$scopedSlots.head && this.$scopedSlots.head(this);
  }

  renderBody(): any {
    return this.$slots.default;
  }

  renderFooter(): any {
    return this.$scopedSlots.footer && this.$scopedSlots.footer(this);
  }

  open() {
    this.isOpen = true;
  }

  hide(): Promise<void> {
    return new Promise(res => {
      this.isOpen = false;
      setTimeout(function() {
        res();
      }, 300);
    })
  }

  mounted() {
    setTimeout(() => this.open(), 1);
  }

  render() {
    const head = this.renderHead();
    const body = this.renderBody();
    const footer = this.renderFooter();
    return (
      <div class={`modal slide-panel is-active ${this.rootClasses.join(" ")} ${this.isOpen ? 'is-open' : ''}`}>
          <div class="modal-background"></div>
          <div class="modal-card card">
            {head != null && (
              <header class={`modal-card-head ${this.headClasses.join(" ")}`}>
                {head}
              </header>
            )}
            {body != null && (
              <section class={`modal-card-body ${this.bodyClasses.join(" ")}`}>
                {body}
              </section>
            )}
            {footer != null && (
              <section class={`modal-card-foot ${this.footClasses.join(" ")}`}>
                {footer}
              </section>
            )}
          </div>
      </div>
    );
  }

}

@Component
class CommitableSlidePanel extends SlidePanel {

  protected isCommited: boolean = false;

  get isCommitable() {
    return true;
  }

  onClickCommit(...args) {}

  onClickCancel() {
    this.cancel();
  }

  protected cancel(args = {}) {
    this.$emit("cancel", args);
  }

  protected commit(args = {}) {
    this.$emit("commit", args);
    this.isCommited = true;
  }

  protected renderExtraFooterActions(){
    return null;
  }

  renderFooter() {
    return (
      <div class="columns is-fluid">
        <div class="column is-9">
          {this.renderExtraFooterActions()}
        </div>
        <div class="column is-3 is-flex justify-end">
          <button class="button is-small is-fluid"
            onClick={this.onClickCancel} disbaled={this.isCommited}>
              {this.isReadonly ? 'Fermer' : 'Annuler'}
            </button>
          {!this.isReadonly && (
            <button class={`button is-small is-fluid is-primary ${loadingClass(this.isCommited)}`}
              disabled={this.isCommitable ? undefined : true}
              onClick={this.onClickCommit}>Valider</button>
          )}
        </div>
      </div>
    );
  }
}
export { CommitableSlidePanel };

type Validator<T> = {
  fn: (d: Partial<T>) => boolean | Promise<boolean>,
  confirm?: (d: Partial<T>) => Promise<boolean>,
  message?: string | VNode,
  enabled?: (d: Partial<T>) => boolean
};

@Component
class CommitableDataSlidePanel<T> extends CommitableSlidePanel {

  @Action notify;

  @Prop({default: () => null}) readonly edit: T;

  protected data: Partial<T> = null;

  /**
   * ex :
   * [
   *  {fn: d => notEmpty(d.prop), message: "Le champ est requis"},
   *  {fn: d => new Promise(res => res(d.prop > 0)), message: "Promise => false"}
   *  {
   *      fn: d => false,
   *      confirm: async _ => await this.promiseConfirmPanel("Hello from confirm ! continue ?")
   *  }
   * ]
   */
  protected validators: Validator<T>[] = [];

  get create(): boolean {
    return !this.edit;
  }

  get mode() {
    return this.edit ? "edit" : "create";
  }

  async loadEditedData() {
    return clone(this.edit, true);
  }

  async created() {
    if (this.mode === "edit") {
      this.data = await this.loadEditedData();
    }
  }

  async onClickCommit() {
    if (await this.doValidate()) {
      this.commit({data: this.data, mode: this.mode, edit: this.edit});
    }
  }

  async doValidate() {
    const fails: Validator<T>[] = (await Promise.all(
      this.validators
        .filter(v => !v.enabled || v.enabled(this.data))
        .map(v => new Promise<Validator<T>>(async (res, rej) => {
          try {
            const out = await v.fn(this.data);
            if (!out) {
            res(v);
            } else {
              res(null);
            }
          } catch (e) { rej(e) }
        }))
    )).filter(Boolean);

    //first we check the confirms
    const confirmed = (await sequentialMapEvery(fails, async v => {
      if (!v.confirm) return true;
      return v.confirm(this.data);
    }));

    if (confirmed) { //check messages
      const messages = fails.map(v => v.message).filter(Boolean);
      if (messages.length) {
        this.notify({
          message: <ul class="validation-error-list">{messages.map(m => (
            <li class="list-item is-airy">
              <Icon icon="warning" class="icon is-small white"/>
              <span>{m}</span>
            </li>
            ))}
          </ul>,
          duration: 12000,
          level: "danger"
        });
      }
      return !messages.length;
    } else {
      return false;
    }
  }

}

export { CommitableDataSlidePanel };

@Component
class CommitableDataSlidePanelWithUploader<T extends CavAbstractDocument> extends CommitableDataSlidePanel<T> {

  protected showLightbox:boolean = false;

  protected onUploadEnd( {file, originalFile, mimeType}){
    this.data.originalFile = originalFile;
    this.data.file = file;
    this.data.mimeType = mimeType;
  }

  protected renderUploader(){
    return (
      <Uploader
        service={ uploadDoc }
        onUploadEnd={this.onUploadEnd}
        value={ this.data as CavAbstractDocument }
      />
    );
  }

  protected renderLightBox(){
    if( this.showLightbox === false ) return null;
    return <LightBox images={ LightBox.mapImages(this.data.getUrl())} onClose={() => this.showLightbox = false} />;
  }

  protected viewFile(){
    this.showLightbox = true;
  }

  protected renderExtraFooterActions(){
    if( ! this.data.hasFile() ) return null;
    return [
      this.data.isImage() && <button class="button is-small" onClick={this.viewFile}>Visualiser le fichier</button>,
      <a class="button is-small" href={this.data.getUrl()} >Télécharger le fichier</a>
    ].filter(Boolean);
  };

}

export {CommitableDataSlidePanelWithUploader};

@Component
class ConfirmSlidePanel extends CommitableSlidePanel {

  @Prop({default: "Annuler"}) readonly cancelButtonLabel!: string;
  @Prop({default: "Ok"}) readonly confirmButtonLabel!: string;

  get isCommitable() {
    return true;
  }

  onClickCommit(...args) {
    this.commit({data: {confirmed: true}});
  }

  onClickCancel() {
    this.cancel();
  }

  protected cancel(args = {}) {
    this.$emit("cancel", args);
  }

  protected commit(args = {}) {
    this.$emit("commit", args);
  }

  renderFooter() {
    return (
      <div class="columns is-fluid">
        <div class="column is-9"></div>
        <div class="column is-3 is-flex justify-end">
          <button class="button is-small is-fluid" onClick={this.onClickCancel}>{this.cancelButtonLabel}</button>
          <button class="button is-small is-fluid is-primary"
            onClick={this.onClickCommit}>{this.confirmButtonLabel}</button>
        </div>
      </div>
    );
  }
}
export { ConfirmSlidePanel };

// VUE JSX HOT LOADER //
if (module.hot) require("/Users/home/j2ee/projects/cavites/src/main/client/node_modules/vue-jsx-hot-loader/src/api.js")({ Vue: require('vue'), ctx: eval('this'), module: module, hotId: "_vue_jsx_hot-65d33d8c/slide-panel.tsx" });